// Справки „Продадени продукти" / „Продадени рецепти" (`kind`): хедър „Справка"
// с подзаглавие, период (7 пресета + календар, по подразбиране „Този месец") и
// Excel; KPI за периода, търсачка (по име), таблица с колони, които сортират,
// филтри от–до В ЗАГЛАВИЯТА (Бруто; при рецептите и Продадено) с ред
// „Филтри: …" над таблицата, „Покажи още" и ред „Общо за периода" (карти на
// телефон). Над таблицата стои постоянното съобщение, че се броят само
// разнесените отчети (+ чакащите и „Разнеси" — `sold/SoldPostedOnlyNotice`), а
// под търсачката — какво отваря клик на ред. Продуктите са теоретично срещу
// реално: „По рецепта" (текущите рецепти × продаденото) · „Разнесено"
// (изписаното от разнесените отчети) ·
// „Разлика" (`sold/SoldProductsTable`, `sold/soldVarianceModel`). Клик/Enter на
// ред отваря панел вдясно (мастър-детайл като складовата карта; под 1024 px —
// лист на цял екран): продукт → складовата му карта за периода
// (`sold/SoldProductPanel`), рецепта → По дни · Отчети · Съставки
// (`sold/SoldRecipePanel`). Продажбите НЕ са по склад.
import { useEffect, useMemo, useRef, useState } from "react";
import { AppstoreOutlined, EuroOutlined, ShoppingOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../hooks/useViewport";
import { useSoldProducts, useSoldRecipes } from "../../../hooks/useInventory";
import { useSoldProductsPosted } from "../../../hooks/useInventorySoldAnalysis";
import type { SoldSellable } from "../../../api/inventory";
import { exportToExcel } from "../../../utils/excelExport";
import { InventoryTheme } from "../components/InventoryTheme";
import { PageHeader } from "../components/list/PageHeader";
import { EmptyScreen, KpiGrid, SearchBox, SectionCard, type KpiCard } from "../components/list/ListBits";
import { ReportToolbar } from "../components/list/ReportToolbar";
import { refreshOf } from "../components/list/reportRefresh";
import { formatPeriod, periodOfPreset, periodToParams, type Period } from "../components/period/periodPresets";
import { FilterSummaryLine } from "../components/filters/FilterSummaryLine";
import { allSections, useColumnFilters, type ColumnSections } from "../components/filters/useColumnFilters";
import { useShowMore } from "../components/list/useShowMore";
import { formatMoney } from "../components/numbers";
import { useUnitDecimals } from "../components/qty";
import { nextSort, type LegacyReportProps } from "./components/reportListModel";
import { ReportLoading, ReportNoMatch, ResultSum, ShowMoreRow } from "./components/ReportListBits";
import {
  EMPTY_SOLD_FILTERS,
  formatCount,
  searchSold,
  soldFilterSpecs,
  soldSortFirstDir,
  soldTotals,
  sortSold,
  type SoldFilterKey,
  type SoldSort,
  type SoldSortKey,
} from "./components/soldReportModel";
import { SoldReportTable } from "./components/SoldReportTable";
import { SoldProductsTable } from "./sold/SoldProductsTable";
import { SoldMasterList } from "./sold/SoldMasterList";
import { SoldProductPanel } from "./sold/SoldProductPanel";
import { SoldRecipePanel } from "./sold/SoldRecipePanel";
import { SoldPostedOnlyNotice } from "./sold/SoldPostedOnlyNotice";
import { SoldDatesHint } from "./sold/SoldVarianceBits";
import { soldExcelColumns } from "./sold/soldExcel";
import { focusSoldPanel, useSoldSelection } from "./sold/useSoldSelection";
import {
  asSoldRows,
  isEmptySoldRow,
  mergeSoldProducts,
  soldVarianceTotals,
  type SoldRow,
} from "./sold/soldVarianceModel";

export type SoldKind = "product" | "recipe";

type Props = LegacyReportProps & { kind: SoldKind };

// Стабилен празен списък — `data = []` по подразбиране е нов масив на всеки рендер
const NO_SOLD: SoldSellable[] = [];

export const SoldReport = ({ kind }: Props) => {
  const { t } = useTranslation();
  const { isNarrow, isDesktop } = useViewport();
  const isProduct = kind === "product";
  // За продукти количеството е в мерната единица → знаци по продукт (decimal_places);
  // за рецепти е БРОЙ продадени (formatCount, без единица).
  const decimalsOf = useUnitDecimals();

  const [period, setPeriod] = useState<Period>(() => periodOfPreset("thisMonth"));
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<SoldSort | null>(null);

  const params = periodToParams(period);
  const productQuery = useSoldProducts(isProduct ? params : null);
  const recipeQuery = useSoldRecipes(isProduct ? null : params);
  const { data: soldRows = NO_SOLD, isLoading, isFetching } = isProduct ? productQuery : recipeQuery;
  // Продуктите: + реално изписаното от разнасянията и разликата (докато
  // реалното се зарежда, колоните му са „…")
  const postedQuery = useSoldProductsPosted(isProduct ? params : null);
  const rows = useMemo<SoldRow[]>(
    () =>
      (isProduct ? mergeSoldProducts(soldRows, postedQuery.data) : asSoldRows(soldRows)).filter(
        (row) => !isEmptySoldRow(row),
      ),
    [isProduct, soldRows, postedQuery.data],
  );

  // ─── Филтри от–до в колоните ─────────────────────────────────────────────
  const specs = useMemo(
    () =>
      soldFilterSpecs<SoldRow>({
        titles: { qty: t("inventoryPage.soldColSold"), gross: t("inventoryPage.colGross") },
        formatMoney,
        formatCount,
      }),
    [t],
  );
  const searched = useMemo(() => searchSold(rows, query), [rows, query]);
  const filters = useColumnFilters({ specs, empty: EMPTY_SOLD_FILTERS, universe: rows, base: searched });
  const shown = useMemo(() => sortSold(filters.rows, sort), [filters.rows, sort]);
  // Низ, не масив — useShowMore нулира при СМЯНА на ключа
  const resetKey = JSON.stringify([kind, formatPeriod(period), query, filters.state, sort]);
  const { visible, hiddenCount, showMore } = useShowMore(shown, resetKey);
  // KPI-тата са за периода; редът над таблицата — за намереното
  const kpis = soldTotals(rows);
  const totals = soldTotals(shown);
  const varianceTotals = soldVarianceTotals(shown);
  const clearAll = () => {
    filters.clearAll();
    setQuery("");
  };
  // Продукти ↔ рецепти може да е същата инстанция на страницата — филтърът по
  // количество не бива да остане скрит и активен при продуктите
  const clearFilters = filters.clearAll;
  useEffect(() => clearFilters(), [kind, clearFilters]);
  // Количеството на продуктите е в различни мерни единици — филтър само при рецептите
  const sections: ColumnSections<SoldFilterKey> = {
    qty: isProduct ? undefined : filters.section("qty"),
    gross: filters.section("gross"),
  };

  const tableFooter = shown.length ? (
    <ShowMoreRow hiddenCount={hiddenCount} onShowMore={showMore} />
  ) : (
    <ReportNoMatch onClear={clearAll} />
  );

  const kpiCards: KpiCard[] = [
    { label: t("inventoryPage.soldKpiCount"), value: String(kpis.count), Icon: AppstoreOutlined },
    { label: t("inventoryPage.soldKpiQty"), value: formatCount(kpis.qty), Icon: ShoppingOutlined },
    { label: t("inventoryPage.soldKpiRevenue"), value: formatMoney(kpis.gross), Icon: EuroOutlined, tone: "brand" },
  ];

  const title = t(isProduct ? "inventoryPage.soldProducts" : "inventoryPage.soldRecipes");
  const exportXls = () =>
    exportToExcel({
      filename: isProduct ? "prodadeni-produkti" : "prodadeni-recepti",
      sheetName: title,
      rows: shown,
      columns: soldExcelColumns(isProduct, t),
    });

  const onSort = (key: SoldSortKey) => setSort((current) => nextSort(current, key, soldSortFirstDir(key)));

  // ─── Панелът вдясно: избраният ред, Esc и фокусът обратно на реда ──────
  const tableRef = useRef<HTMLDivElement | null>(null);
  const selection = useSoldSelection({ scopeKey: kind, allRows: rows, visibleRows: visible, tableRef });
  const selected = selection.selected;
  const paneOpen = !!selected && isDesktop;
  const panel = (row: SoldRow, variant: "pane" | "sheet") =>
    isProduct ? (
      <SoldProductPanel
        row={row}
        period={period}
        variant={variant}
        decimals={decimalsOf(row.sellableId)}
        onClose={selection.close}
      />
    ) : (
      <SoldRecipePanel row={row} period={period} variant={variant} onClose={selection.close} />
    );

  return (
    <InventoryTheme>
      <div className="relative flex h-full flex-col bg-canvas text-ink">
        <PageHeader
          kicker={t("inventoryPage.kickerReport")}
          title={title}
          subtitle={
            isLoading
              ? undefined
              : t(isProduct ? "inventoryPage.soldSubtitleProduct" : "inventoryPage.soldSubtitleRecipe", {
                  count: kpis.count,
                })
          }
          right={
            <ReportToolbar
              period={period}
              onPeriodChange={setPeriod}
              filterSections={allSections(sections)}
              onExport={exportXls}
              exportDisabled={!shown.length}
              onRefresh={refreshOf(isProduct ? productQuery : recipeQuery).refresh}
              refreshing={isFetching}
            />
          }
        />
        <div className="flex min-h-0 flex-1">
        <div
          className={`flex min-h-0 min-w-0 flex-1 flex-col gap-3.5 overflow-y-auto ${
            isNarrow ? "px-3 pb-10 pt-3.5" : paneOpen ? "px-5 pb-12 pt-5" : "px-7 pb-12 pt-5"
          }`}
        >
          {/* Панелът е отворен: KPI-тата отстъпват място на списъка (както в „Наличности") */}
          {!paneOpen && <KpiGrid cards={kpiCards} />}
          <div className="flex flex-col gap-1.5">
            <div className="flex flex-wrap items-center gap-2">
              <SearchBox value={query} onChange={setQuery} placeholder={t("inventoryPage.soldSearchPh")} />
            </div>
            {/* Какво отваря клик на ред — редът иначе подсказва само с курсора */}
            {rows.length > 0 && !paneOpen && (
              <p className="m-0 text-[12.5px] leading-[1.4] text-body">
                {t(isProduct ? "inventoryPage.soldOpenHintProduct" : "inventoryPage.soldOpenHintRecipe")}
              </p>
            )}
          </div>
          <SoldPostedOnlyNotice />
          <SectionCard className={isFetching && !isLoading ? "opacity-60" : ""}>
            <FilterSummaryLine
              countLabel={t("inventoryPage.reportResult", { count: shown.length })}
              items={filters.summary}
              onClearAll={filters.clearAll}
            >
              <ResultSum label={t("inventoryPage.colGross")} value={formatMoney(totals.gross)} strong />
            </FilterSummaryLine>
            {isProduct && rows.length > 0 && !isLoading && !paneOpen && <SoldDatesHint />}
            {isLoading ? (
              <ReportLoading />
            ) : rows.length === 0 ? (
              <EmptyScreen Icon={ShoppingOutlined} tone="brand" title={t("inventoryPage.noSold")} />
            ) : paneOpen && selected ? (
              // Панелът е отворен: таблицата отстъпва на тесния списък
              <SoldMasterList
                rows={visible}
                isProduct={isProduct}
                selectedId={selected.sellableId}
                decimalsOf={decimalsOf}
                onSelect={selection.select}
                onEnterPanel={focusSoldPanel}
                footer={<ShowMoreRow hiddenCount={hiddenCount} onShowMore={showMore} />}
              />
            ) : (
              <div ref={tableRef}>
                {isProduct ? (
                  <SoldProductsTable
                    rows={visible}
                    isCards={isNarrow}
                    decimalsOf={decimalsOf}
                    sort={sort}
                    onSort={onSort}
                    filters={sections}
                    onOpen={selection.open}
                    totals={varianceTotals}
                    footer={tableFooter}
                  />
                ) : (
                  <SoldReportTable
                    rows={visible}
                    isCards={isNarrow}
                    sort={sort}
                    onSort={onSort}
                    filters={sections}
                    onOpen={selection.open}
                    totals={totals}
                    footer={tableFooter}
                  />
                )}
              </div>
            )}
          </SectionCard>
        </div>
        {paneOpen && selected && (
          <aside className="flex w-[48%] min-w-[440px] max-w-[820px] shrink-0 flex-col">{panel(selected, "pane")}</aside>
        )}
        </div>

        {/* Под 1024 px панелът е лист на цял екран; справката остава отдолу */}
        {selected && !isDesktop && (
          <div className="absolute inset-0 z-30 flex flex-col bg-canvas">{panel(selected, "sheet")}</div>
        )}
      </div>
    </InventoryTheme>
  );
};

export default SoldReport;
